import { TFunction } from 'i18next'
import { Link } from 'react-router-dom'
import z from 'zod'

import { NextLeaseBadge, ResidentCell } from '@/components/business/units'
import { Badge } from '@/components/ui/badge'
import { DEFAULT_LIMIT } from '@/components/ui/pagination/pagination.constants'
import { SelectOption } from '@/components/ui/select'
import { TableColumn } from '@/components/ui/table'
import { SortOrderSchema } from '@/components/ui/table/table.types'
import { Tooltip } from '@/components/ui/tooltip'
import { Typography } from '@/components/ui/typography'
import { EMPTY_VALUE } from '@/constants'
import { routerFactory } from '@/factories'
import { LeaseStatus, LeaseStatusEnum, UnitListItem } from '@/models/features'
import { formatDateLong } from '@/utils/date'
import { getLeaseHolder, getResidentFullName } from '@/utils/units'

import * as S from './units.page.styled'
import { UnitOccupancyTabEnum } from './units.page.types'
import {
  getLeaseStatusColor,
  getLeaseStatusLabel,
  getUnitStatusColor,
  getUnitStatusLabel,
} from './units.page.utils'

export const UNITS_TABLE_ID = 'units'

export const getLeaseStatusOptions = (t: TFunction<['units']>): SelectOption<LeaseStatus>[] => [
  { label: t('units:leaseStatus.current'), value: 'Current' },
  { label: t('units:leaseStatus.future'), value: 'Future' },
  { label: t('units:leaseStatus.past'), value: 'Past' },
  { label: t('units:leaseStatus.notice'), value: 'Notice' },
  { label: t('units:leaseStatus.applicant'), value: 'Applicant' },
]

export const unitsQueryFiltersSchema = z.object({
  fromDate: z.coerce.number().optional(),
  leaseStatus: z.array(LeaseStatusEnum).optional(),
  limit: z.coerce.number().optional(),
  search: z.coerce.string().optional(),
  skip: z.coerce.number().optional(),
  sortBy: z.string().optional(),
  sortOrder: SortOrderSchema.optional(),
  status: UnitOccupancyTabEnum.optional(),
  toDate: z.coerce.number().optional(),
})

export type UnitsQueryFilters = z.infer<typeof unitsQueryFiltersSchema>

export const unitsInitialFilters: UnitsQueryFilters = {
  fromDate: undefined,
  leaseStatus: undefined,
  limit: DEFAULT_LIMIT,
  search: '',
  skip: 0,
  sortBy: 'unit',
  sortOrder: 'asc',
  status: UnitOccupancyTabEnum.enum.all,
  toDate: undefined,
}

interface GetUnitsColumnsProps {
  t: TFunction<['units']>
}

// Note: the backend keeps move-in/out and lease dates on the lease, not on the unit row.
const getNextLease = (unit: UnitListItem) => unit.futureLeases[0] ?? null

export const getUnitsColumns = ({ t }: GetUnitsColumnsProps): TableColumn<UnitListItem>[] => [
  {
    id: 'unitNumber',
    render: (unit) => unit.unit,
    sortKey: 'unit',
    title: t('units:list.columns.unitNumber'),
    width: '12rem',
  },
  {
    id: 'unitType',
    render: (unit) => unit.unitType || EMPTY_VALUE,
    title: t('units:list.columns.unitType'),
    width: '15rem',
  },
  {
    id: 'unitStatus',
    render: (unit) => (
      <Badge color={getUnitStatusColor(unit.occupancyStatus)} variant="light" size="small">
        {getUnitStatusLabel(unit.occupancyStatus, t)}
      </Badge>
    ),
    title: t('units:list.columns.unitStatus'),
    width: '14rem',
  },
  {
    id: 'leaseHolder',
    render: (unit) => {
      const leaseHolder = getLeaseHolder(unit.residents)
      if (!leaseHolder) return EMPTY_VALUE

      const otherResidents = unit.residents.filter((resident) => resident !== leaseHolder)

      return (
        <S.ResidentsCell>
          <ResidentCell resident={leaseHolder} />
          {otherResidents.length > 0 && (
            <Tooltip
              placement="bottom"
              trigger={
                <Badge color="blue" size="small" variant="solid">
                  {`+${otherResidents.length}`}
                </Badge>
              }
            >
              <S.TooltipTitle>{t('units:list.residentsTooltip.title')}</S.TooltipTitle>
              <S.TooltipList>
                {otherResidents.map((resident) => (
                  <li key={resident.clientId ?? resident.email}>
                    <Typography as="span" size="text-medium" weight="medium" color="white">
                      {resident.clientId === null ? (
                        getResidentFullName(resident) || resident.email
                      ) : (
                        <Link
                          to={routerFactory.getPathString({
                            params: { clientId: resident.clientId },
                            path: '/dashboard/clients/:clientId',
                          })}
                          style={{ color: 'inherit', textDecoration: 'underline' }}
                          onClick={(event) => event.stopPropagation()}
                        >
                          {getResidentFullName(resident) || resident.email}
                        </Link>
                      )}
                    </Typography>
                  </li>
                ))}
              </S.TooltipList>
            </Tooltip>
          )}
        </S.ResidentsCell>
      )
    },
    title: t('units:list.columns.leaseHolder'),
    width: '28rem',
  },
  {
    id: 'moveInDate',
    render: (unit) => formatDateLong(unit.currentLease?.moveInDate),
    sortKey: 'moveInDate',
    title: t('units:list.columns.moveIn'),
    width: '13rem',
  },
  {
    id: 'moveOutDate',
    render: (unit) => formatDateLong(unit.currentLease?.moveOutDate),
    sortKey: 'moveOutDate',
    title: t('units:list.columns.moveOut'),
    width: '13rem',
  },
  {
    id: 'leaseStatus',
    render: (unit) => {
      const nextLease = getNextLease(unit)
      const nextLeaseStart = nextLease?.leaseStart ?? nextLease?.moveInDate
      const leaseStatus = unit.currentLease?.leaseStatus ?? nextLease?.leaseStatus

      if (!leaseStatus) return EMPTY_VALUE

      return (
        <S.LeaseStatusCell>
          <Badge color={getLeaseStatusColor(leaseStatus)} variant="light" size="small">
            {getLeaseStatusLabel(leaseStatus, t)}
          </Badge>
          {!!nextLease && (
            <NextLeaseBadge
              tooltip={t('units:list.nextLeaseTooltip', {
                date: formatDateLong(nextLeaseStart),
              })}
            />
          )}
        </S.LeaseStatusCell>
      )
    },
    title: t('units:list.columns.leaseStatus'),
    width: '15rem',
  },
  {
    id: 'leaseStartDate',
    render: (unit) => formatDateLong(unit.currentLease?.leaseStart),
    sortKey: 'leaseStart',
    title: t('units:list.columns.leaseStart'),
    width: '13rem',
  },
  {
    id: 'leaseEndDate',
    render: (unit) => formatDateLong(unit.currentLease?.leaseEnd),
    sortKey: 'leaseEnd',
    title: t('units:list.columns.leaseEnd'),
    width: '13rem',
  },
]
